import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react'
import { supabase } from '../services/supabase'

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email) {
      setError('Email is required')
      return
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      })

      if (error) {
        console.error('Password reset error:', error)
        setError(`Failed to send reset email: ${error.message}`)
        setIsLoading(false)
        return
      }

      setIsSuccess(true)
      setMessage('Password reset email sent! Please check your inbox and spam folder.')
      setIsLoading(false)

    } catch (err) {
      console.error('Unexpected error:', err)
      setError('An unexpected error occurred. Please try again.')
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-space-dark via-space-darker to-black flex items-center justify-center p-4">
        {/* Starfield Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="stars"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="glass-card p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-4">Check Your Email</h1>
            <p className="text-gray-300 mb-6">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-gray-400 text-sm mb-6">
              Don't see the email? Check your spam folder or wait a few minutes for it to arrive.
            </p>
            
            <div className="space-y-4">
              <Link
                to="/login"
                className="block w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors"
              >
                Back to Login
              </Link>
              
              <button
                onClick={() => {
                  setIsSuccess(false)
                  setEmail('')
                  setMessage('')
                }}
                className="block w-full text-gray-400 hover:text-white transition-colors"
              >
                Try different email
              </button>
            </div>

            <div className="mt-6">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-dark via-space-darker to-black flex items-center justify-center p-4">
      {/* Starfield Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="stars"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Reset Password</h1>
            <p className="text-gray-400">Enter your email to receive a reset link</p>
          </div>

          {/* Reset Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setError('')
                    setMessage('')
                  }}
                  autoComplete="email"
                  className={`w-full pl-10 pr-4 py-3 bg-space-darker/50 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors text-white placeholder-gray-400 ${
                    error ? 'border-red-500' : 'border-gray-600'
                  }`}
                  placeholder="commander@crownofSuns.galaxy"
                />
              </div>
              {error && <p className="mt-1 text-sm text-red-400">{error}</p>}
              {message && <p className="mt-1 text-sm text-green-400">{message}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-800 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending Reset Link...
                </>
              ) : (
                <>
                  <Mail className="w-5 h-5" />
                  Send Reset Link
                </>
              )}
            </button>
          </form>

          {/* Alternative Actions */}
          <div className="mt-6 text-center space-y-3">
            <p className="text-gray-400">
              Remember your password?{' '}
              <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors">
                Sign in here
              </Link>
            </p>
            
            <p className="text-gray-400">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-400 hover:text-primary-300 transition-colors">
                Register here
              </Link>
            </p>
          </div>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ForgotPassword